import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3-force';

const ANIMATION_DURATION = 500; // Animation duration in ms

interface Circle {
  id: string;
  x: number;
  y: number;
  vx?: number;
  vy?: number;
  color: string;
  animatedRadius: number;
  animationStartTime?: number | undefined;
}

interface DotsClusterProps {
  dots: Circle[];
  radius: number;
  width: number;
  height: number;
}

export const DotsCluster = ({
  dots,
  radius,
  width,
  height,
}: DotsClusterProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [circles, setCircles] = useState<Circle[]>([]);
  const circlesRef = useRef<Circle[]>(circles);
  const simulationRef = useRef<d3.Simulation<Circle, undefined> | null>(null);
  const animationRef = useRef<number>();

  // Helper function to calculate animated radius without mutation
  const getAnimatedRadius = useCallback(
    (circle: Circle): number => {
      if (!circle.animationStartTime || circle.animatedRadius >= radius) {
        return circle.animatedRadius;
      }

      const elapsed = Date.now() - circle.animationStartTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      return (
        circle.animatedRadius + progress * (radius - circle.animatedRadius)
      );
    },
    [radius],
  );

  // Create a new simulation
  useEffect(() => {
    const simulation = d3
      .forceSimulation(circles)
      .alphaTarget(0.1) // little movement at rest
      .velocityDecay(0.1) // low friction
      .force('x', d3.forceX(width / 2).strength(0.005)) // Center force x
      .force('y', d3.forceY(height / 2).strength(0.005)) // Center force y
      .force(
        'collide', // Keep dots from overlapping
        d3
          .forceCollide<Circle>()
          .radius((d) => radius + 1)
          .iterations(3),
      );

    simulationRef.current = simulation;

    return () => {
      simulation.stop();
    };
  }, [width, height, radius, circles]);

  // Update the simulation when the circles change
  useEffect(() => {
    circlesRef.current = circles; // Keep ref in sync with state
    if (simulationRef.current) {
      simulationRef.current.nodes([...circles]);
      simulationRef.current.restart();
    }
  }, [circles]);

  // React to dots changes from parent component
  useEffect(() => {
    setCircles((prevCircles) => {
      // Add only new dots that are not already present in prevCircles (by id)
      const existingIds = new Set(prevCircles.map((c) => c.id));

      const newCircles = dots
        .filter((dot) => !existingIds.has(dot.id))
        .map((dot) => ({
          ...dot,
          // Randomize start position
          x: dot.x + Math.random() * width,
          y: dot.y + Math.random() * height,
          animationStartTime:
            dot.animatedRadius < radius ? Date.now() : undefined,
        }));

      return [...prevCircles, ...newCircles];
    });
  }, [dots, radius, width, height]);

  // Animation loop for canvas rendering
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const animate = () => {
      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw circles
      circlesRef.current.forEach((circle) => {
        const currentRadius = getAnimatedRadius(circle);

        if (currentRadius > 0) {
          ctx.beginPath();
          ctx.arc(circle.x, circle.y, currentRadius, 0, 2 * Math.PI);
          ctx.fillStyle = circle.color;
          ctx.fill();
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [width, height, radius, getAnimatedRadius]);

  return <canvas ref={canvasRef} width={width} height={height} />;
};
